import { useRouter } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useThemePalette } from '@/config/theme';
import { useMyPredictions, usePredictionTally, useSavePrediction } from '@/features/predictions/game';
import { useAuth } from '@/lib/auth/AuthProvider';
import type { FixtureWithRefs } from '@/lib/data/provider';
import { formatNumber } from '@/lib/i18n/format';
import { localizeTeamName } from '@/lib/i18n/footballNames';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import type { PredictionPick } from '@/lib/supabase/types';
import { errorKey } from '@/lib/utils/errors';

const POINTS_PER_CORRECT = 3;
type Pick = 'home' | 'draw' | 'away';

function PickButton({
  label,
  selected,
  disabled,
  onPress,
}: {
  label: string;
  selected: boolean;
  disabled: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected, disabled }}
      disabled={disabled}
      onPress={onPress}
      className={`h-10 flex-1 items-center justify-center rounded-full border ${
        selected ? 'border-volt bg-volt' : 'border-line bg-inset active:bg-surface-2'
      } ${disabled && !selected ? 'opacity-50' : ''}`}
    >
      <AppText
        variant="micro"
        weight="bold"
        numberOfLines={1}
        className={selected ? 'text-on-volt' : 'text-soft'}
      >
        {label}
      </AppText>
    </Pressable>
  );
}

/** Community sentiment: a 3-segment bar + per-outcome %, with the user's pick lit. */
function PollBar({
  tally,
  myPick,
  homeName,
  awayName,
}: {
  tally: { home: number; draw: number; away: number; total: number };
  myPick: Pick | undefined;
  homeName: string;
  awayName: string;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const pct = (n: number) => (tally.total > 0 ? Math.round((n / tally.total) * 100) : 0);
  const cols: { key: Pick; label: string; color: string; value: number }[] = [
    { key: 'home', label: homeName, color: palette.win, value: tally.home },
    { key: 'draw', label: t('predictions.draw'), color: palette.draw, value: tally.draw },
    { key: 'away', label: awayName, color: palette.loss, value: tally.away },
  ];
  return (
    <View className="gap-2 border-t border-line pt-3">
      <View className="h-2 flex-row overflow-hidden rounded-full bg-inset">
        {cols.map((c) => (
          <View key={c.key} style={{ flex: Math.max(c.value, c.value === 0 ? 0 : 1), backgroundColor: c.color }} />
        ))}
      </View>
      <View className="flex-row items-start">
        {cols.map((c, index) => (
          <View
            key={c.key}
            className={`flex-1 gap-0.5 ${index === 0 ? 'items-start' : index === 1 ? 'items-center' : 'items-end'}`}
          >
            <AppText
              variant="micro"
              weight={myPick === c.key ? 'bold' : 'medium'}
              numberOfLines={1}
              style={{ color: myPick === c.key ? c.color : palette.muted }}
            >
              {c.label}
            </AppText>
            <AppText variant="caption" weight="bold" tabular style={{ color: c.color }}>
              {formatNumber(pct(c.value), numeralOptions)}%
            </AppText>
          </View>
        ))}
      </View>
      <AppText variant="micro" className="text-center text-muted">
        {t('predictions.tallyCount', { count: tally.total })}
      </AppText>
    </View>
  );
}

/**
 * The user's own 1X2 pick for THIS match (the points game). Pickable while the
 * match is scheduled and before kickoff; locked once it starts. Below the picker
 * a community poll shows how everyone is leaning (counts + %), so you can see
 * what the crowd expects before you commit.
 */
export function MatchPickWidget({ fixture }: { fixture: FixtureWithRefs }) {
  const router = useRouter();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { userId } = useAuth();
  const myPredictions = useMyPredictions();
  const saveMutation = useSavePrediction();
  const tallyQuery = usePredictionTally(fixture.id);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [now] = useState(() => Date.now());

  // Auto-clear the error line, cancelling the timer if we unmount or it changes.
  useEffect(() => {
    if (!feedback) return;
    const timer = setTimeout(() => setFeedback(null), 2500);
    return () => clearTimeout(timer);
  }, [feedback]);

  const myPick = myPredictions.byFixture.get(fixture.id);
  const locked =
    fixture.status !== 'scheduled' || new Date(fixture.kickoff_at).getTime() <= now;
  const tally = tallyQuery.data;

  // Nothing to pick and no crowd data to show - keep the screen clean.
  if (locked && !myPick && !(tally && tally.total > 0)) return null;

  const homeName = localizeTeamName(fixture.home_team, locale, true);
  const awayName = localizeTeamName(fixture.away_team, locale, true);

  const choose = (pick: PredictionPick) => {
    if (!userId) {
      router.push('/auth/sign-in');
      return;
    }
    saveMutation.mutate(
      { fixtureId: fixture.id, pick },
      { onError: (error) => setFeedback(t(errorKey(error))) },
    );
  };

  const statusText =
    myPick && locked
      ? myPick.points === null
        ? t('predictions.locked')
        : myPick.points > 0
          ? t('predictions.correct', { points: myPick.points })
          : t('predictions.missed')
      : myPick
        ? `${t('predictions.yourPick')} ✓`
        : locked
          ? t('predictions.locked')
          : t('predictions.pointsPerCorrect', { points: POINTS_PER_CORRECT });

  return (
    <Card className="mx-4 mb-4 gap-3 px-4 py-3.5">
      <View className="flex-row items-center justify-between">
        <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-muted">
          {t('predictions.yourPrediction')}
        </AppText>
        <AppText variant="micro" className="text-soft" numberOfLines={1}>
          {statusText}
        </AppText>
      </View>
      <View className="flex-row items-center gap-2">
        <PickButton
          label={homeName}
          selected={myPick?.pick === 'home'}
          disabled={locked || saveMutation.isPending}
          onPress={() => choose('home')}
        />
        <PickButton
          label={t('predictions.draw')}
          selected={myPick?.pick === 'draw'}
          disabled={locked || saveMutation.isPending}
          onPress={() => choose('draw')}
        />
        <PickButton
          label={awayName}
          selected={myPick?.pick === 'away'}
          disabled={locked || saveMutation.isPending}
          onPress={() => choose('away')}
        />
      </View>
      {tally && tally.total > 0 ? (
        <PollBar tally={tally} myPick={myPick?.pick} homeName={homeName} awayName={awayName} />
      ) : null}
      {feedback ? (
        <AppText variant="micro" className="text-loss">
          {feedback}
        </AppText>
      ) : null}
      {!userId ? (
        <AppText variant="micro" className="text-soft">
          {t('predictions.signInToPlay')}
        </AppText>
      ) : null}
    </Card>
  );
}
